import {useEffect, useRef, useState} from "react";
import {
  Phone,
  Mail,
  MapPin,
  Tag,
  PhoneOutgoing,
  Search,
  Send,
  Plus,
  FileText,
  Download,
} from "lucide-react";
import moment from "moment-timezone";

// Renders a WhatsApp media message (image/video/audio/document/sticker).
// `msg.mediaUrl` is a presigned S3 URL that amazonconnect-contactinfo's
// /get-chat-transcript resolves server-side from the chat transcript's
// native Connect ATTACHMENT entry (Connect stores WhatsApp media as a real
// chat attachment; there's no caption for these today).
const WhatsAppMedia = ({msg, bubbleIsCustomer}) => {
  const captionClass = bubbleIsCustomer ? "text-white" : "text-gray-800";

  switch (msg.mediaType) {
    case "image":
    case "sticker":
      return (
        <div className="space-y-1">
          <img
            src={msg.mediaUrl}
            alt={msg.caption || "WhatsApp image"}
            className="max-w-[240px] max-h-[240px] rounded-lg object-cover"
          />
          {msg.caption && <p className={captionClass}>{msg.caption}</p>}
        </div>
      );
    case "video":
      return (
        <div className="space-y-1">
          <video
            controls
            src={msg.mediaUrl}
            className="max-w-[240px] rounded-lg"
          />
          {msg.caption && <p className={captionClass}>{msg.caption}</p>}
        </div>
      );
    case "audio":
      return (
        <div className="space-y-1">
          <audio controls src={msg.mediaUrl} className="max-w-[240px]" />
          {msg.caption && <p className={captionClass}>{msg.caption}</p>}
        </div>
      );
    case "document":
      return (
        <a
          href={msg.mediaUrl}
          target="_blank"
          rel="noreferrer"
          className={`flex items-center gap-2 underline ${captionClass}`}
        >
          <FileText size={16} className="shrink-0" />
          <span className="truncate">{msg.fileName || "Document"}</span>
          <Download size={14} className="shrink-0" />
        </a>
      );
    default:
      return <p>{msg.Content}</p>;
  }
};

// Common dial codes for the "New Conversation" country picker, ISO-keyed so
// US/Canada (both +1) don't collide as select values. Ordered with the most
// frequently-dialed regions for this contact center first.
const COUNTRY_CODES = [
  {iso: "US", name: "United States", dialCode: "+1", flag: "🇺🇸"},
  {iso: "CA", name: "Canada", dialCode: "+1", flag: "🇨🇦"},
  {iso: "MX", name: "Mexico", dialCode: "+52", flag: "🇲🇽"},
  {iso: "GB", name: "United Kingdom", dialCode: "+44", flag: "🇬🇧"},
  {iso: "AU", name: "Australia", dialCode: "+61", flag: "🇦🇺"},
  {iso: "IN", name: "India", dialCode: "+91", flag: "🇮🇳"},
  {iso: "PH", name: "Philippines", dialCode: "+63", flag: "🇵🇭"},
  {iso: "CN", name: "China", dialCode: "+86", flag: "🇨🇳"},
  {iso: "VN", name: "Vietnam", dialCode: "+84", flag: "🇻🇳"},
  {iso: "DE", name: "Germany", dialCode: "+49", flag: "🇩🇪"},
  {iso: "FR", name: "France", dialCode: "+33", flag: "🇫🇷"},
  {iso: "ES", name: "Spain", dialCode: "+34", flag: "🇪🇸"},
  {iso: "IT", name: "Italy", dialCode: "+39", flag: "🇮🇹"},
  {iso: "BR", name: "Brazil", dialCode: "+55", flag: "🇧🇷"},
  {iso: "RU", name: "Russia", dialCode: "+7", flag: "🇷🇺"},
  {iso: "JP", name: "Japan", dialCode: "+81", flag: "🇯🇵"},
  {iso: "KR", name: "South Korea", dialCode: "+82", flag: "🇰🇷"},
  {iso: "SO", name: "Somalia", dialCode: "+252", flag: "🇸🇴"},
  {iso: "ET", name: "Ethiopia", dialCode: "+251", flag: "🇪🇹"},
  {iso: "NG", name: "Nigeria", dialCode: "+234", flag: "🇳🇬"},
  {iso: "MH", name: "Marshall Islands", dialCode: "+692", flag: "🇲🇭"},
];

export const WhatsAppIcon = ({className, onClick}) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    onClick={onClick}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const OrbitLoader = () => (
  <div className="relative h-10 w-10 animate-spin">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#2ba9cd] rounded-full"></div>
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
  </div>
);

const DetailItem = ({icon: Icon, label, value}) => (
  <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg shadow-sm">
    <Icon className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#2ba9cd]" />
    <div className="flex flex-col">
      <span className="text-xs font-semibold uppercase text-gray-500">
        {label}
      </span>
      <span className="text-sm font-medium text-gray-800 break-words">
        {value || "--"}
      </span>
    </div>
  </div>
);

const WhatsAppMessageModal = ({phone: initialPhone, onClose}) => {
  const isNewConversation = !initialPhone;
  const [phone, setPhone] = useState(initialPhone || "");
  const [countryIso, setCountryIso] = useState("US");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorText, setErrorText] = useState("");

  // Message template picker - "" means the free-text box below is used
  // instead (messageType "text"). WhatsApp only allows business-initiated
  // messages (i.e. a brand new conversation) to use an approved template,
  // so a new conversation auto-selects the first one once the catalog loads.
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateDetail, setTemplateDetail] = useState(null);
  const [templateDetailLoading, setTemplateDetailLoading] = useState(false);
  const [templateParams, setTemplateParams] = useState([]);

  const selectedCountry =
    COUNTRY_CODES.find((c) => c.iso === countryIso) || COUNTRY_CODES[0];
  // For an existing conversation, initialPhone already comes in fully
  // formatted (e.g. from a prior interaction) - only prefix a dial code
  // when the agent is dialing a bare national number for a new one.
  const fullPhone = isNewConversation
    ? `${selectedCountry.dialCode}${phone.trim().replace(/\D/g, "")}`
    : phone.trim();

  const usingTemplate = !!selectedTemplateId;

  useEffect(() => {
    let cancelled = false;
    setTemplatesLoading(true);
    fetch(`${process.env.REACT_APP_AWS_ENDPOINT}/whatsapp-templates`, {
      headers: {Authorization: process.env.REACT_APP_AUTH_TOKEN},
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setTemplates(data.templates || []);
      })
      .catch(() => {
        if (!cancelled) setTemplatesError("Failed to load message templates.");
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isNewConversation && !selectedTemplateId && templates.length > 0) {
      setSelectedTemplateId(templates[0].metaTemplateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  useEffect(() => {
    if (!selectedTemplateId) {
      setTemplateDetail(null);
      setTemplateParams([]);
      return;
    }
    let cancelled = false;
    setTemplateDetailLoading(true);
    fetch(
      `${process.env.REACT_APP_AWS_ENDPOINT}/whatsapp-templates/${selectedTemplateId}`,
      {headers: {Authorization: process.env.REACT_APP_AUTH_TOKEN}},
    )
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setTemplateDetail(data);
        setTemplateParams(Array(data.placeholderCount || 0).fill(""));
      })
      .catch(() => {
        if (!cancelled) setErrorText("Failed to load the template's details.");
      })
      .finally(() => {
        if (!cancelled) setTemplateDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTemplateId]);

  const templateParamsFilled = templateParams.every((p) => p.trim());
  const canSend =
    phone.trim() &&
    (!isNewConversation || phone.trim().replace(/\D/g, "").length >= 6) &&
    status !== "sending" &&
    (usingTemplate
      ? !!templateDetail && !templateDetailLoading && templateParamsFilled
      : !!message.trim());

  const handleSend = async () => {
    if (!canSend) return;
    setStatus("sending");
    setErrorText("");
    try {
      const payload = usingTemplate
        ? {
            destinationPhoneNumber: fullPhone,
            messageType: "template",
            templateName: templateDetail.templateName,
            templateLanguage: templateDetail.templateLanguage,
            templateParameters: templateParams.map((p) => p.trim()).join("|"),
          }
        : {
            destinationPhoneNumber: fullPhone,
            messageType: "text",
            messageBody: message.trim(),
          };
      const res = await fetch(
        `${process.env.REACT_APP_AWS_ENDPOINT}/send-whatsapp`,
        {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus("success");
      } else {
        setErrorText(data.errorMessage || "Failed to send message.");
        setStatus("error");
      }
    } catch {
      setErrorText("Network error. Please try again.");
      setStatus("error");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center space-x-2 mb-4">
          <WhatsAppIcon className="w-6 h-6 text-[#25D366]" />
          <h3 className="text-xl font-semibold text-gray-800">
            {isNewConversation ? "New WhatsApp Conversation" : "Send WhatsApp"}
          </h3>
        </div>

        {isNewConversation && status !== "success" ? (
          <div className="mb-4">
            <div className="flex gap-2">
              <select
                value={countryIso}
                onChange={(e) => setCountryIso(e.target.value)}
                aria-label="Country code"
                className="w-28 shrink-0 p-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-[#25D366] focus:border-[#25D366] outline-none"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.iso} value={c.iso}>
                    {c.flag} {c.dialCode}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="5551234567"
                className="flex-1 min-w-0 p-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-1 focus:ring-[#25D366] focus:border-[#25D366] outline-none"
              />
            </div>
            <p className="mt-1 text-xs text-gray-400 font-mono">{fullPhone}</p>
          </div>
        ) : (
          <p className="text-sm text-gray-500 mb-4 font-mono">{phone}</p>
        )}

        {status === "success" ? (
          <div className="flex flex-col items-center space-y-4 py-4">
            <div className="text-[#25D366] text-4xl">✓</div>
            <p className="text-gray-700 font-medium">
              Message sent successfully!
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 rounded-lg text-sm"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Message Template
              </label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                disabled={templatesLoading}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-[#25D366] focus:border-[#25D366] outline-none disabled:opacity-50"
              >
                <option value="">
                  {isNewConversation
                    ? "— Select a template —"
                    : "✏️ Custom message"}
                </option>
                {templates.map((t) => (
                  <option key={t.metaTemplateId} value={t.metaTemplateId}>
                    {t.templateName} ({t.templateLanguage})
                  </option>
                ))}
              </select>
              {templatesLoading && (
                <p className="mt-1 text-xs text-gray-400">
                  Loading templates…
                </p>
              )}
              {templatesError && (
                <p className="mt-1 text-xs text-red-500">{templatesError}</p>
              )}
              {isNewConversation && (
                <p className="mt-1 text-xs text-gray-400">
                  A brand-new conversation must start with an approved
                  template.
                </p>
              )}
            </div>

            {usingTemplate ? (
              templateDetailLoading || !templateDetail ? (
                <p className="text-xs text-gray-400 italic">
                  Loading template…
                </p>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 whitespace-pre-wrap">
                    {templateDetail.bodyText.replace(
                      /\{\{(\d+)\}\}/g,
                      (match, n) =>
                        templateParams[Number(n) - 1]?.trim() || match,
                    )}
                  </div>
                  {templateParams.map((val, idx) => (
                    <input
                      key={idx}
                      type="text"
                      value={val}
                      onChange={(e) =>
                        setTemplateParams((prev) =>
                          prev.map((p, i) =>
                            i === idx ? e.target.value : p,
                          ),
                        )
                      }
                      placeholder={
                        templateDetail.exampleParameters[idx]
                          ? `e.g. ${templateDetail.exampleParameters[idx]}`
                          : `{{${idx + 1}}}`
                      }
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-[#25D366] focus:border-[#25D366] outline-none"
                    />
                  ))}
                </div>
              )
            ) : (
              <textarea
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message..."
                className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-1 focus:ring-[#25D366] focus:border-[#25D366] outline-none text-sm"
              />
            )}
            {status === "error" && (
              <p className="text-red-500 text-xs">{errorText}</p>
            )}
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={!canSend}
                className="flex items-center space-x-2 px-4 py-2 bg-[#25D366] text-white rounded-lg text-sm disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                <span>{status === "sending" ? "Sending…" : "Send"}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* --- MISSED WHATSAPP SUB-VIEW --- */

const GroupedWhatsAppList = ({
  groups,
  searchTerm,
  setSearchTerm,
  selectedPhone,
  onSelect,
}) => (
  <div className="flex flex-col bg-white border-r border-gray-200 h-full">
    <div className="p-4 border-b flex-shrink-0">
      <h2 className="text-xl font-bold text-gray-800 flex items-center">
        <Phone className="w-5 h-5 mr-2 text-[#2ba9cd]" />
        WhatsApp by Number
      </h2>
      <div className="relative mt-2 mb-3">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>
    </div>
    <div className="flex-1 min-h-0 overflow-y-auto">
      {groups.length > 0 ? (
        groups.map((group) => (
          <div
            key={group.phone}
            className={`px-3 py-2 space-y-1 cursor-pointer transition border-b last:border-b-0
              ${
                selectedPhone === group.phone
                  ? "bg-[#E7F7FB] border-[#2ba9cd] border-l-4"
                  : "hover:bg-gray-50 border-l-4 border-[#E7F7FB]"
              }`}
            onClick={() => onSelect(group)}
          >
            <div className="flex justify-between items-center">
              <div className="flex flex-col">
                <p className="text-sm font-medium text-gray-900">
                  {group.name}
                </p>
                <p className="text-xs text-gray-400">{group.phone}</p>
              </div>
              <div className="flex flex-col items-end space-y-1">
                <span className="text-xs bg-[#2ba9cd] text-white rounded-full px-2 py-0.5">
                  {group.contacts.length}
                </span>
                {group.hasHighlight && (
                  <div className="h-2 w-2 bg-red-600 rounded-full shadow-[0_0_8px_rgba(220,38,38,0.5)]" />
                )}
              </div>
            </div>
          </div>
        ))
      ) : (
        <p className="p-4 text-center text-gray-500 text-sm italic">
          {searchTerm
            ? `No matches for "${searchTerm}".`
            : "No interactions found."}
        </p>
      )}
    </div>
  </div>
);

const GroupedWhatsAppPane = ({group, setIsModalOpen, onWhatsApp}) => {
  if (!group) {
    return (
      <div className="flex h-full items-center justify-center text-gray-500">
        <p className="text-lg">Select a phone number from the list.</p>
      </div>
    );
  }

  const first = group.contacts[0];
  return (
    <div className="flex flex-col bg-white h-full overflow-y-auto p-4">
      <div className="flex justify-between items-start border-b pb-3 mb-4">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 mb-1">
            {group.name}
          </h1>
          <p className="flex items-center text-lg text-[#2ba9cd] font-mono">
            {group.phone}
            <PhoneOutgoing
              className="w-5 h-5 ml-2 cursor-pointer text-red-500"
              onClick={() => setIsModalOpen(true)}
            />
            <WhatsAppIcon
              className="w-5 h-5 ml-2 cursor-pointer text-[#25D366] hover:opacity-75"
              onClick={() => onWhatsApp(group.phone)}
            />
          </p>
        </div>
        <span className="text-sm text-gray-500">
          {group.contacts.length} message
          {group.contacts.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <DetailItem icon={Mail} label="Email" value={first.customer_email} />
        <DetailItem
          icon={Tag}
          label="Program"
          value={first.client_name?.toLowerCase()}
        />
        <DetailItem icon={MapPin} label="City" value={first.customer_city} />
        <DetailItem icon={MapPin} label="State" value={first.customer_state} />
      </div>

      <h3 className="text-xl font-semibold text-gray-700 mb-3 border-b pb-1">
        Messages
      </h3>
      <div className="space-y-3 overflow-y-auto">
        {group.contacts.map((contact) => (
          <div
            key={contact.ContactId}
            className="p-3 bg-[#E7F7FB] rounded-lg border border-[#2ba9cd]"
          >
            <p className="text-xs text-gray-500 mb-1">
              {moment
                .tz(contact.start_time, "America/Mexico_City")
                .format("MM/DD/YYYY hh:mm A")}
            </p>
            <p className="text-sm text-gray-800">
              {contact.initial_message || "--"}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const MissedWhatsAppView = ({agentQueues, notifications, makeDirectOutboundCall, onNotificationRead}) => {
  const [missedWhatsApp, setMissedWhatsApp] = useState([]);
  const [selectedWhatsAppGroup, setSelectedWhatsAppGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [whatsAppPhone, setWhatsAppPhone] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [queueOptions, setQueueOptions] = useState([
    {value: "", name: "--Please Select--"},
  ]);

  const filteredWhatsApp = (() => {
    if (!searchTerm) return missedWhatsApp;
    const lowerCaseSearch = searchTerm.toLowerCase();
    return missedWhatsApp.filter(
      (item) =>
        item.customer_phonenumber.toLowerCase().includes(lowerCaseSearch) ||
        item.customer_firstname.toLowerCase().includes(lowerCaseSearch) ||
        item.customer_lastname.toLowerCase().includes(lowerCaseSearch) ||
        item.client_name.toLowerCase().includes(lowerCaseSearch),
    );
  })();

  const groupedWhatsApp = Object.values(
    filteredWhatsApp.reduce((acc, item) => {
      const key = item.customer_phonenumber;
      if (!acc[key]) {
        acc[key] = {
          phone: key,
          name:
            `${item.customer_firstname} ${item.customer_lastname}`.trim() ||
            key,
          latestTime: item.start_time,
          hasHighlight: false,
          contacts: [],
        };
      }
      acc[key].contacts.push(item);
      if (new Date(item.start_time) > new Date(acc[key].latestTime)) {
        acc[key].latestTime = item.start_time;
      }
      if (item.highlighted) acc[key].hasHighlight = true;
      return acc;
    }, {}),
  ).sort((a, b) => new Date(b.latestTime) - new Date(a.latestTime));

  const getMissedWhatsApp = async () => {
    if (isInitialLoad) {
      setIsLoading(true);
    }

    try {
      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_AUTH_TOKEN,
        },
      };

      const waRes = await fetch(
        `${process.env.REACT_APP_AWS_ENDPOINT}/get-missed-whatsapp`,
        options,
      );
      const waData = await waRes.json();

      let queues =
        agentQueues?.map((x) => x?.name?.toLowerCase()).filter(Boolean) || [];

      const filtered =
        queues.length > 0
          ? waData.filter((x) => queues.includes(x.client_name?.toLowerCase()))
          : waData;
      const processedWaData = filtered.sort(
        (a, b) => new Date(b.start_time) - new Date(a.start_time),
      );

      const updatedWaData = processedWaData.map((wa) => ({
        ...wa,
        highlighted: notifications.missedWhatsApp.some(
          (n) => n.ContactId === wa.ContactId,
        ),
      }));

      setMissedWhatsApp(updatedWaData);

      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    } catch (error) {
      console.error("Error fetching missed WhatsApp interactions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (agentQueues?.length > 0) {
      setQueueOptions([
        {value: "", name: "--Please Select--"},
        ...agentQueues.filter(Boolean),
      ]);
    }
  }, [agentQueues]);

  useEffect(() => {
    getMissedWhatsApp();
  }, [agentQueues, notifications]);

  useEffect(() => {
    const exists = groupedWhatsApp.some(
      (g) => g.phone === selectedWhatsAppGroup?.phone,
    );
    if (!exists || !selectedWhatsAppGroup) {
      setSelectedWhatsAppGroup(
        groupedWhatsApp.length > 0 ? groupedWhatsApp[0] : null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredWhatsApp, groupedWhatsApp]);

  const handleSubmit = () => {
    setIsModalOpen(false);
    makeDirectOutboundCall(selectedWhatsAppGroup?.phone, selectedQueue);
  };

  const handleGroupSelect = (group) => {
    setSelectedWhatsAppGroup(group);

    const contactsToMark = group.contacts.filter((contact) =>
      notifications.missedWhatsApp.some(
        (n) => n.ContactId === contact.ContactId,
      ),
    );

    contactsToMark.forEach((contact) => {
      const options = {
        method: "PATCH",
        body: JSON.stringify({
          ContactId: contact.ContactId,
          source_notification: "MissedWhatsApp",
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_AUTH_TOKEN,
        },
      };
      fetch(`${process.env.REACT_APP_AWS_ENDPOINT}/notifications`, options)
        .then((resp) => resp.json())
        .then(() => onNotificationRead?.())
        .catch((error) => console.error("Error marking read:", error));
    });
  };

  return (
    <>
      {!isLoading ? (
        <div className="flex flex-1 min-h-0 bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="w-1/3 flex flex-col">
            <GroupedWhatsAppList
              groups={groupedWhatsApp}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedPhone={selectedWhatsAppGroup?.phone}
              onSelect={handleGroupSelect}
            />
          </div>
          <div className="w-2/3 bg-white flex flex-col">
            <GroupedWhatsAppPane
              group={selectedWhatsAppGroup}
              setIsModalOpen={setIsModalOpen}
              onWhatsApp={setWhatsAppPhone}
            />
          </div>

          {isModalOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
              onClick={() => setIsModalOpen(false)}
            >
              <div
                className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-2xl font-semibold mb-4">Outbound Call</h3>
                <div className="space-y-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium mb-1">
                      Target Queue
                    </label>
                    <select
                      value={selectedQueue}
                      onChange={(e) => setSelectedQueue(e.target.value)}
                      className="p-2 border rounded-lg"
                    >
                      {queueOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2 bg-gray-200 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={!selectedQueue}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50"
                    >
                      Call
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex h-full justify-center items-center">
          <OrbitLoader />
        </div>
      )}

      {whatsAppPhone && (
        <WhatsAppMessageModal
          phone={whatsAppPhone}
          onClose={() => setWhatsAppPhone(null)}
        />
      )}
    </>
  );
};

/* --- WHATSAPP MESSAGES SUB-VIEW --- */

const MessagesWhatsAppView = () => {
  const [whatsAppContacts, setWhatsAppContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [transcriptMap, setTranscriptMap] = useState({});
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptCountMap, setTranscriptCountMap] = useState({});
  // Synchronous mirror of transcriptMap for the polling effect below, so it
  // can check "do we already have this contact cached" without depending on
  // (and re-running for) every transcriptMap update.
  const latestTranscriptMapRef = useRef({});

  useEffect(() => {
    const fetchWhatsApp = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_AWS_ENDPOINT}/get-whatsapp-contacts`,
          {
            headers: {Authorization: process.env.REACT_APP_AUTH_TOKEN},
          },
        );
        const data = await response.json();

        if (data.status_code === 200) {
          data.contacts.sort(
            (a, b) => new Date(b.start_time) - new Date(a.start_time),
          );
          setWhatsAppContacts(data.contacts || []);
        }
      } catch (error) {
        console.error("Failed to fetch WhatsApp contacts:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWhatsApp();
  }, []);

  const groupedWhatsApp = (() => {
    const lower = searchTerm.toLowerCase();
    const filtered = lower
      ? whatsAppContacts.filter(
          (item) =>
            item.customer_phonenumber?.toLowerCase().includes(lower) ||
            `${item.customer_firstname || ""} ${item.customer_lastname || ""}`
              .toLowerCase()
              .includes(lower),
        )
      : whatsAppContacts;

    return Object.values(
      filtered.reduce((acc, item) => {
        const key = item.customer_phonenumber;
        if (!acc[key]) {
          acc[key] = {
            phone: key,
            name:
              `${item.customer_firstname || ""} ${item.customer_lastname || ""}`.trim() ||
              key,
            latestTime: item.start_time,
            contacts: [],
          };
        }
        acc[key].contacts.push(item);
        if (new Date(item.start_time) > new Date(acc[key].latestTime)) {
          acc[key].latestTime = item.start_time;
        }
        return acc;
      }, {}),
    ).sort((a, b) => new Date(b.latestTime) - new Date(a.latestTime));
  })();

  useEffect(() => {
    const exists = groupedWhatsApp.some(
      (g) => g.phone === selectedGroup?.phone,
    );
    if (!exists) {
      setSelectedGroup(groupedWhatsApp.length > 0 ? groupedWhatsApp[0] : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedWhatsApp]);

  useEffect(() => {
    if (!selectedGroup) return;

    // Connect can take a while to finish writing a chat's transcript to S3
    // after messages arrive (and especially after the chat ends) - that
    // delay is on Connect's side, not ours. Poll in the background so newly
    // available messages/attachments show up on their own instead of
    // requiring a manual page reload.
    //
    // This account's Lambda concurrency limit is very low (10, shared across
    // every function in the account). Fetching all of a contact's sessions
    // at once (Promise.all) can easily blow past that on its own and get
    // throttled, which silently looks like "missing" messages since a
    // failed fetch just renders as empty. Fetch a few sessions at a time
    // instead to stay well under that ceiling.
    const CONCURRENCY = 2;
    let cancelled = false;

    const fetchOne = async (contact) => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_AWS_ENDPOINT}/get-chat-transcript`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: process.env.REACT_APP_AUTH_TOKEN,
            },
            body: JSON.stringify({contactId: contact.ContactId}),
          },
        );
        const data = await res.json();
        return [
          contact.ContactId,
          data.status_code === 200 ? data.transcript || [] : [],
        ];
      } catch {
        return [contact.ContactId, []];
      }
    };

    const fetchTranscripts = async (silent) => {
      // A closed session's transcript is done changing - once we've loaded
      // it, there's no need to keep re-fetching it on every poll or every
      // time this conversation is reselected. Only (re)fetch sessions that
      // are still open (no end_time yet) or that we've genuinely never
      // loaded before, so switching back to an already-seen conversation is
      // instant and background polls stay cheap.
      const contacts = selectedGroup.contacts;
      const alreadyCached = (id) =>
        Object.prototype.hasOwnProperty.call(latestTranscriptMapRef.current, id);
      const toFetch = contacts.filter(
        (c) => !c.end_time || !alreadyCached(c.ContactId),
      );

      const haveNothingCached = contacts.every((c) => !alreadyCached(c.ContactId));
      if (!silent && haveNothingCached) {
        setTranscriptLoading(true);
      }

      const results = [];
      for (let i = 0; i < toFetch.length; i += CONCURRENCY) {
        if (cancelled) return;
        const batch = toFetch.slice(i, i + CONCURRENCY);
        results.push(...(await Promise.all(batch.map(fetchOne))));
      }

      if (cancelled) return;

      setTranscriptMap((prev) => {
        const next = {...prev, ...Object.fromEntries(results)};
        latestTranscriptMapRef.current = next;

        const withMessages = contacts.filter((c) => {
          const msgs = next[c.ContactId] || [];
          return msgs.length > 0 || !!c.initial_message;
        }).length;
        setTranscriptCountMap((prevCounts) => ({
          ...prevCounts,
          [selectedGroup.phone]: withMessages,
        }));

        return next;
      });
      setTranscriptLoading(false);
    };

    fetchTranscripts(false);
    const intervalId = setInterval(() => fetchTranscripts(true), 60000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [selectedGroup]);

  return (
    <div className="flex flex-1 min-h-0 bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="w-1/3 border-r flex flex-col min-h-0">
        <div className="p-4 border-b flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-800 flex items-center">
            <WhatsAppIcon className="w-5 h-5 mr-2 text-[#25D366]" />
            WhatsApp Contacts
          </h2>
          <div className="relative mt-2 mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 pl-10 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#2ba9cd]"
            />
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center p-10">
              <OrbitLoader />
            </div>
          ) : groupedWhatsApp.length === 0 ? (
            <p className="p-6 text-center text-gray-400 italic text-sm">
              {searchTerm
                ? `No matches for "${searchTerm}".`
                : "No WhatsApp contacts found."}
            </p>
          ) : (
            groupedWhatsApp.map((group) => (
              <div
                key={group.phone}
                className={`px-3 py-2 cursor-pointer transition border-b last:border-b-0
                  ${
                    selectedGroup?.phone === group.phone
                      ? "bg-[#E7F7FB] border-l-4 border-l-[#2ba9cd]"
                      : "hover:bg-gray-50 border-l-4 border-l-transparent"
                  }`}
                onClick={() => setSelectedGroup(group)}
              >
                <div className="flex justify-between items-center">
                  <div className="flex flex-col min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {group.name}
                    </p>
                    <p className="text-xs text-gray-400">{group.phone}</p>
                    <p className="text-xs text-gray-400">
                      {moment
                        .tz(group.latestTime, "America/Mexico_City")
                        .format("Do MMM hh:mm A")}
                    </p>
                  </div>
                  <span className="text-xs bg-[#2ba9cd] text-white rounded-full px-2 py-0.5 flex-shrink-0 ml-2">
                    {transcriptCountMap[group.phone] !== undefined
                      ? transcriptCountMap[group.phone]
                      : group.contacts.length}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="w-2/3 flex flex-col overflow-y-auto p-4">
        {!selectedGroup ? (
          <div className="flex h-full items-center justify-center text-gray-400">
            <p className="text-sm italic">Select a contact from the list.</p>
          </div>
        ) : transcriptLoading ? (
          <div className="flex h-full items-center justify-center">
            <OrbitLoader />
          </div>
        ) : (
          <>
            <div className="border-b pb-3 mb-4 flex-shrink-0">
              <p className="text-lg font-bold text-gray-900">
                {selectedGroup.name}
              </p>
              <p className="text-sm text-[#2ba9cd] font-mono">
                {selectedGroup.phone}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {selectedGroup.contacts.length} session
                {selectedGroup.contacts.length !== 1 ? "s" : ""}
              </p>
            </div>

            <div className="space-y-6">
              {selectedGroup.contacts.map((contact) => {
                const messages = transcriptMap[contact.ContactId] || [];
                const hasTranscript = messages.length > 0;
                const hasInitial = !!contact.initial_message;
                if (!hasTranscript && !hasInitial) return null;

                const displayMessages = hasTranscript
                  ? messages
                  : [
                      {
                        Id: `initial-${contact.ContactId}`,
                        ParticipantRole: "CUSTOMER",
                        Content: contact.initial_message,
                        AbsoluteTime: contact.start_time,
                      },
                    ];

                return (
                  <div key={contact.ContactId}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {moment
                          .tz(contact.start_time, "America/Mexico_City")
                          .format("Do MMM YYYY hh:mm A")}
                        {contact.client_name ? ` · ${contact.client_name}` : ""}
                      </span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>

                    <div className="space-y-2 bg-gray-50 rounded-xl p-3">
                      {displayMessages.map((msg, idx) => (
                        <div
                          key={msg.Id || idx}
                          className={`flex ${
                            msg.ParticipantRole === "CUSTOMER"
                              ? "justify-end"
                              : "justify-start"
                          }`}
                        >
                          <div
                            className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm shadow-sm ${
                              msg.ParticipantRole === "CUSTOMER"
                                ? "bg-[#2ba9cd] text-white rounded-tr-none"
                                : "bg-white border border-gray-200 text-gray-800 rounded-tl-none"
                            }`}
                          >
                            {msg.mediaType ? (
                              <WhatsAppMedia
                                msg={msg}
                                bubbleIsCustomer={
                                  msg.ParticipantRole === "CUSTOMER"
                                }
                              />
                            ) : (
                              <p>{msg.Content}</p>
                            )}
                            <p
                              className={`text-[10px] mt-1 ${
                                msg.ParticipantRole === "CUSTOMER"
                                  ? "text-white/70 text-right"
                                  : "text-gray-400"
                              }`}
                            >
                              {msg.AbsoluteTime
                                ? moment
                                    .tz(msg.AbsoluteTime, "America/Mexico_City")
                                    .format("hh:mm A")
                                : ""}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* --- MAIN COMPONENT --- */

const WhatsApp = ({agentQueues, notifications, makeDirectOutboundCall, onNotificationRead}) => {
  const [subTab, setSubTab] = useState("Missed");
  const [isNewConversationOpen, setIsNewConversationOpen] = useState(false);

  return (
    <div className="flex flex-col p-4 space-y-6 h-full">
      <div className="flex justify-between p-4 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <WhatsAppIcon className="w-6 h-6 mr-2 text-[#25D366]" />
          WhatsApp {subTab === "Missed" ? "Missed Messages" : "Messages"}
        </h2>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsNewConversationOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-[#25D366] text-white rounded-lg text-sm hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            <span>New Conversation</span>
          </button>
          <label className="text-sm font-medium text-gray-700">
            View Logs For:
          </label>
          <select
            value={subTab}
            onChange={(e) => setSubTab(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-1 focus:ring-[#2ba9cd] outline-none"
          >
            <option value="Missed">Missed Messages</option>
            <option value="Messages">Messages</option>
          </select>
        </div>
      </div>

      {subTab === "Missed" ? (
        <MissedWhatsAppView
          agentQueues={agentQueues}
          notifications={notifications}
          makeDirectOutboundCall={makeDirectOutboundCall}
          onNotificationRead={onNotificationRead}
        />
      ) : (
        <MessagesWhatsAppView />
      )}

      {isNewConversationOpen && (
        <WhatsAppMessageModal onClose={() => setIsNewConversationOpen(false)} />
      )}
    </div>
  );
};

export default WhatsApp;
